import { useMemo, useState } from 'react';
import moment from 'moment';
import debounce from 'lodash.debounce';
import { useTranslation } from '@packlink/translation-provider';
import { DatePicker, Grid, GridChild, ISelectOption, Option, Select, Input, Icon, IconSize } from '@shipengine/giger';
import { IconNames } from '@shipengine/giger-theme';
import { useAmplitude } from '@hooks';
import { AmplitudeEvents, AmplitudePropertiesPro } from '@constants/amplitude';
import { getDatePickerStyles, getFiltersStyles } from './SettingsTransactionsStyles';

const API_DATE_FORMAT = 'YYYY-MM-DD';

const RangeOptions = {
    CUSTOM: 'custom',
    LAST_30_DAYS: 'last-30-days',
    THIS_MONTH: 'this-month',
    LAST_MONTH: 'last-month',
};

export type TransactionFilters = {
    id?: string;
    from?: string;
    to?: string;
};

type TransactionsFiltersProps = {
    filters: TransactionFilters;
    onFiltersChange: (filters: TransactionFilters) => void;
};

export function TransactionsFilters({ filters, onFiltersChange }: TransactionsFiltersProps): JSX.Element {
    const {
        t,
        i18n: { language: locale },
    } = useTranslation();
    const { sendAmplitudeEvent } = useAmplitude();

    const [searchByIdInputValue, setSearchByIdInputValue] = useState(filters.id ?? '');
    const [selectedOption, setSelectedOption] = useState(getInitialSelectedOption(filters));

    const debouncedIdSearch = useMemo(
        () =>
            debounce((newFilters: TransactionFilters) => {
                sendAmplitudeEvent(AmplitudeEvents.TRANSACTIONS_SEARCH, {
                    [AmplitudePropertiesPro.SEARCH_QUERY]: 'id',
                });

                return onFiltersChange(newFilters);
            }, 300),
        [onFiltersChange, sendAmplitudeEvent],
    );

    const selectedRange = useMemo((): [Date, Date] | undefined => {
        if (selectedOption === RangeOptions.CUSTOM && filters.from && filters.to) {
            return [moment(filters.from, API_DATE_FORMAT).toDate(), moment(filters.to, API_DATE_FORMAT).toDate()];
        }
        return undefined;
    }, [selectedOption, filters.from, filters.to]);

    const rangeOptions = useMemo(
        () => [
            {
                label: t('transactions.filter.custom'),
                value: RangeOptions.CUSTOM,
            },
            {
                label: t('transactions.filter.last-30-days'),
                value: RangeOptions.LAST_30_DAYS,
            },
            {
                label: t('transactions.filter.this-month'),
                value: RangeOptions.THIS_MONTH,
            },
            {
                label: t('transactions.filter.last-month'),
                value: RangeOptions.LAST_MONTH,
            },
        ],
        [t],
    );

    const labels = useMemo(
        () => ({
            selectDateIconTooltip: t('datepicker.labels.select-date'),
            previousMonthTooltip: t('datepicker.labels.previous-month'),
            nextMonthTooltip: t('datepicker.labels.next-month'),
            cancelButton: t('datepicker.labels.cancel'),
            confirmButton: t('datepicker.labels.confirm'),
        }),
        [t],
    );

    const onDateRangeChange = (_: string, option: ISelectOption | null) => {
        if (!option) return;

        setSelectedOption(option.value);
        sendAmplitudeEvent(AmplitudeEvents.TRANSACTIONS_SEARCH, {
            [AmplitudePropertiesPro.SEARCH_QUERY]: option.value,
        });

        if (option.value !== RangeOptions.CUSTOM) {
            const dateRange = getDateRangeForOption(option.value);

            onFiltersChange({
                id: filters.id,
                ...dateRange,
            });
        }
    };

    const onCustomDateConfirm = (date: [Date, Date]) => {
        const [fromDate, toDate] = date;

        onFiltersChange({
            id: filters.id,
            from: moment(fromDate).format(API_DATE_FORMAT),
            to: moment(toDate).format(API_DATE_FORMAT),
        });
    };

    const onSearchById = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setSearchByIdInputValue(value);

        if (!value.length || value.length > 3) {
            debouncedIdSearch({
                id: value,
                from: filters.from,
                to: filters.to,
            });
        }
    };

    const onClearDateFilter = () => {
        setSelectedOption(undefined);

        onFiltersChange({
            id: filters.id,
            from: undefined,
            to: undefined,
        });
    };

    return (
        <Grid noPadding css={getFiltersStyles}>
            <GridChild colSpan={12} colSpanTablet={4}>
                <Input
                    name="id"
                    label={t('transactions.filter.search-by-id')}
                    value={searchByIdInputValue}
                    onChange={onSearchById}
                    leftContent={<Icon name={IconNames.SEARCH} size={IconSize.SIZE_MEDIUM} />}
                />
            </GridChild>

            <GridChild colSpan={12} colSpanTablet={3}>
                <Select
                    label={t('transactions.filter.date-range')}
                    value={
                        selectedOption
                            ? { label: t(`transactions.filter.${selectedOption}`), value: selectedOption }
                            : undefined
                    }
                    name="dateRange"
                    onChange={onDateRangeChange}
                    onClear={onClearDateFilter}
                >
                    {rangeOptions.map((item) => (
                        <Option key={item.value} value={item} selected={selectedOption === item.value}>
                            {item.label}
                        </Option>
                    ))}
                </Select>
            </GridChild>

            {selectedOption === RangeOptions.CUSTOM && (
                <GridChild colSpan={12} colSpanTablet={3} css={getDatePickerStyles}>
                    <DatePicker
                        name="datePicker"
                        label={t('transactions.filter.date-range')}
                        labels={labels}
                        locale={locale}
                        selected={selectedRange}
                        selectsRange
                        monthsShown={2}
                        showPreviousMonths
                        maxDate={new Date()}
                        onClickConfirm={onCustomDateConfirm}
                    />
                </GridChild>
            )}
        </Grid>
    );
}

function getInitialSelectedOption(filters: TransactionFilters) {
    if (filters.from && filters.to) {
        const today = moment();
        const from = moment(filters.from, API_DATE_FORMAT);
        const to = moment(filters.to, API_DATE_FORMAT);

        // Check if it's last 30 days
        const last30From = moment().subtract(30, 'days');
        if (from.isSame(last30From, 'day') && to.isSame(today, 'day')) {
            return RangeOptions.LAST_30_DAYS;
        }

        // Check if it's this month
        const thisMonthFrom = moment().startOf('month');
        if (from.isSame(thisMonthFrom, 'day') && to.isSame(today, 'day')) {
            return RangeOptions.THIS_MONTH;
        }

        // Check if it's last month
        const lastMonthFrom = moment().subtract(1, 'month').startOf('month');
        const lastMonthTo = moment().subtract(1, 'month').endOf('month');
        if (from.isSame(lastMonthFrom, 'day') && to.isSame(lastMonthTo, 'day')) {
            return RangeOptions.LAST_MONTH;
        }

        // It's a custom range
        return RangeOptions.CUSTOM;
    }

    return undefined;
}

function getDateRangeForOption(option: string) {
    const today = moment();

    switch (option) {
        case RangeOptions.LAST_30_DAYS:
            return {
                from: moment().subtract(30, 'days').format(API_DATE_FORMAT),
                to: today.format(API_DATE_FORMAT),
            };
        case RangeOptions.THIS_MONTH:
            return {
                from: moment().startOf('month').format(API_DATE_FORMAT),
                to: today.format(API_DATE_FORMAT),
            };
        case RangeOptions.LAST_MONTH:
            return {
                from: moment().subtract(1, 'month').startOf('month').format(API_DATE_FORMAT),
                to: moment().subtract(1, 'month').endOf('month').format(API_DATE_FORMAT),
            };
        default:
            return {
                from: moment().subtract(30, 'days').format(API_DATE_FORMAT),
                to: today.format(API_DATE_FORMAT),
            };
    }
}
